// Web Audio playback engine for the multi-track editor.
//
// Each unique source file is decoded once into an AudioBuffer and
// cached. Playing the project schedules one AudioBufferSourceNode per
// non-muted clip, routed through a per-track GainNode → master gain →
// destination. Stop/pause cancels every scheduled source.
//
// The engine intentionally does NOT subscribe to the store: callers
// pass the project snapshot to play(), and any layout edit during
// playback should call stop() before scheduling fresh sources. Per-
// track gain/mute can be tweaked live via setTrackGain / muteTrack
// without re-scheduling — that's the whole reason we use Web Audio.
//
// Pitch-preserved speed control: when playbackRate != 1 we pre-render
// a time-stretched copy of every source buffer using soundtouchjs
// (SimpleFilter + WebAudioBufferSource). The stretched buffer's
// internal duration is original/rate, so we still drive playback with
// the normal AudioBufferSourceNode scheduler — just with offset/when/
// playFor divided by the rate. Stretched buffers are cached per
// (filePath, rate).

import { SoundTouch, SimpleFilter, WebAudioBufferSource } from 'soundtouchjs';

const sourceCache = new Map(); // filePath → AudioBuffer (shared across engine instances)
const stretchedCache = new Map(); // `${filePath}::${rate}` → AudioBuffer

// Discrete playback-rate ladder used by the +/− shortcuts. .25 → 4x.
export const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4];

export function nextPlaybackRate(rate, dir) {
  const idx = PLAYBACK_RATES.indexOf(rate);
  if (idx < 0) {
    // Snap an off-ladder rate to the nearest rung, then step.
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < PLAYBACK_RATES.length; i++) {
      const d = Math.abs(PLAYBACK_RATES[i] - rate);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return PLAYBACK_RATES[Math.max(0, Math.min(PLAYBACK_RATES.length - 1, best + (dir > 0 ? 1 : -1)))];
  }
  return PLAYBACK_RATES[Math.max(0, Math.min(PLAYBACK_RATES.length - 1, idx + (dir > 0 ? 1 : -1)))];
}

let _ctx = null;
function ctx() {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  _ctx = new Ctor();
  return _ctx;
}

// Decodes in progress, so a file is never fetched twice. The editor warms
// the audio in the background as soon as a project opens; pressing play
// before that finishes asks for the very same files. Without this map both
// callers downloaded and decoded the whole take independently — the second
// press looked like "it is decoding all over again", because it was.
const inFlight = new Map(); // filePath → Promise<AudioBuffer>

// Takes are tens of megabytes each. Fetching and decoding every track at once
// competes for bandwidth, memory and CPU, so the first file finishes far later
// than it needs to. Three at a time keeps the pipe full without the pile-up.
const MAX_PARALLEL_DECODES = 3;
let activeDecodes = 0;
const decodeQueue = [];

function pumpDecodes() {
  while (activeDecodes < MAX_PARALLEL_DECODES && decodeQueue.length) {
    const { task, resolve, reject } = decodeQueue.shift();
    activeDecodes += 1;
    task().then(resolve, reject).finally(() => { activeDecodes -= 1; pumpDecodes(); });
  }
}

function queued(task) {
  return new Promise((resolve, reject) => {
    decodeQueue.push({ task, resolve, reject });
    pumpDecodes();
  });
}

function decodeFile(filePath) {
  const cached = sourceCache.get(filePath);
  if (cached) return Promise.resolve(cached);
  const pending = inFlight.get(filePath);
  if (pending) return pending;

  const job = queued(async () => {
    // Checked again inside the queue: by the time a queued job starts, an
    // earlier one may already have put this file in the cache.
    const warm = sourceCache.get(filePath);
    if (warm) return warm;
    const buf = await window.api.audio.readBuffer(filePath);
    // decodeAudioData takes ownership of the ArrayBuffer; clone so a retry
    // still has bytes to work with.
    const ab = await ctx().decodeAudioData(buf.slice(0));
    sourceCache.set(filePath, ab);
    return ab;
  }).finally(() => inFlight.delete(filePath));

  inFlight.set(filePath, job);
  return job;
}

export function clearAudioCache() {
  sourceCache.clear();
  stretchedCache.clear();
  inFlight.clear();
}

// Decode every unique source referenced by a project into the shared
// cache (no time-stretch). Standalone twin of AudioEngine#prepare so the
// offline export renderer can guarantee buffers are warm without
// constructing a live engine. Resolves once every source is cached (or
// rejects with the first decode failure). Reuses the shared sourceCache,
// so when the editor already decoded the sources this is effectively a
// no-op.
export async function ensureProjectDecoded(project) {
  const files = new Set();
  for (const t of (project && project.tracks) || []) {
    if (t.sourceFile) files.add(t.sourceFile);
  }
  const results = await Promise.allSettled([...files].map((f) => decodeFile(f)));
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length) {
    const r = failed[0].reason;
    throw new Error(r && r.message ? r.message : 'decode failed');
  }
}

// Sample rate of decoded source buffers. decodeAudioData resamples every
// file to the shared AudioContext's rate, so rendering an OfflineAudioContext
// at this rate avoids a second resample of the cached buffers.
export function sourceSampleRate() {
  return ctx().sampleRate;
}

// Render a pitch-preserved time-stretched copy of `buffer` at `rate`.
// Uses soundtouchjs synchronously (no ScriptProcessor / OfflineAudio
// roundtrip): we pull processed interleaved samples out of SimpleFilter
// in blocks until the source drains, then pack them back into a fresh
// stereo AudioBuffer at the original sample rate. The output length is
// roughly original_length / rate (stretched_duration = orig / rate).
//
// Synchronous and CPU-heavy — wrap in queueMicrotask / setTimeout(0)
// when calling on the UI thread for very long buffers to keep the
// renderer responsive.
function renderStretchedBuffer(buffer, rate) {
  const source = new WebAudioBufferSource(buffer);
  const st = new SoundTouch();
  st.tempo = rate;
  const filter = new SimpleFilter(source, st);

  const BLOCK = 4096;
  const interleaved = new Float32Array(BLOCK * 2);
  // Reasonable upper bound; we trim at the end.
  const estLen = Math.ceil((buffer.length / rate) * 1.05) + BLOCK;
  const left = new Float32Array(estLen);
  const right = new Float32Array(estLen);
  let outPos = 0;
  let extracted;
  do {
    extracted = filter.extract(interleaved, BLOCK);
    for (let i = 0; i < extracted; i++) {
      left[outPos + i] = interleaved[i * 2];
      right[outPos + i] = interleaved[i * 2 + 1];
    }
    outPos += extracted;
  } while (extracted > 0);

  const ab = ctx().createBuffer(2, Math.max(1, outPos), buffer.sampleRate);
  ab.copyToChannel(left.subarray(0, outPos), 0);
  ab.copyToChannel(right.subarray(0, outPos), 1);
  return ab;
}

// Get (or build + cache) a stretched version of the buffer for `rate`.
// rate === 1 returns the original buffer unchanged.
function getOrBuildStretched(filePath, rate) {
  if (rate === 1) return sourceCache.get(filePath) || null;
  const key = `${filePath}::${rate}`;
  if (stretchedCache.has(key)) return stretchedCache.get(key);
  const orig = sourceCache.get(filePath);
  if (!orig) return null;
  const out = renderStretchedBuffer(orig, rate);
  stretchedCache.set(key, out);
  return out;
}

// Look up an already-decoded AudioBuffer for the given source file.
// Returns null when the file hasn't been decoded yet (the editor's
// `prepare()` walks the project on mount, so by the time clips are
// rendering the buffers are usually warm). Used by the waveform
// renderer in the multi-track editor and the script-edit view.
export function getCachedAudioBuffer(filePath) {
  return sourceCache.get(filePath) || null;
}

// True when the file is decoded, or a decode for it is already running. The
// editor uses this to decide whether pressing play has any waiting to show.
export function isAudioReadyOrPending(filePath) {
  return sourceCache.has(filePath) || inFlight.has(filePath);
}

// One-shot isolated preview of a [srcStart, srcEnd] slice of a single
// source file. Routes straight to the AudioContext destination,
// bypassing the project's track gain / mute / solo state so a muted
// hidden region still plays here. Starting a new preview cancels any
// previous one — there's exactly one in flight at a time.
//
// Returns a `stop` function (also auto-fires onEnd when the slice
// finishes naturally). Returns null when the buffer hasn't been
// decoded yet — the caller should surface a "wait for decode" hint.
let _activePreview = null;
export function stopActivePreview() {
  if (_activePreview) {
    try { _activePreview.node.stop(); } catch {}
    try { _activePreview.node.disconnect(); } catch {}
    if (_activePreview.timer) clearTimeout(_activePreview.timer);
    _activePreview = null;
  }
}
export function previewSourceRange(filePath, srcStart, srcEnd, { onEnd } = {}) {
  const ab = sourceCache.get(filePath);
  if (!ab) return null;
  const dur = Math.max(0, srcEnd - srcStart);
  if (dur < 0.01) return null;
  stopActivePreview();
  const c = ctx();
  if (c.state === 'suspended') { try { c.resume(); } catch {} }
  const node = c.createBufferSource();
  node.buffer = ab;
  node.connect(c.destination);
  try {
    node.start(0, Math.max(0, srcStart), dur);
  } catch {
    return null;
  }
  const handle = { node, timer: null };
  _activePreview = handle;
  handle.timer = setTimeout(() => {
    if (_activePreview === handle) {
      try { node.disconnect(); } catch {}
      _activePreview = null;
      if (onEnd) onEnd();
    }
  }, dur * 1000 + 30);
  return () => {
    if (_activePreview === handle) stopActivePreview();
  };
}

// Compute a min/max peaks array for [start, end] of an AudioBuffer,
// downsampled to `peakCount` buckets. The output is interleaved
// [min0, max0, min1, max1, ...] over channel 0 — that's enough for
// the symmetrical waveform we draw (mono / first-channel only is the
// common visual convention for spoken-dialogue clips).
//
// Bucket boundaries snap to integer sample indices; tiny clips with
// fewer samples than peakCount get truncated to the available count.
// This keeps the canvas-rendering loop cheap (no per-pixel resampling)
// and is recomputed whenever the clip's pixel width changes by more
// than a small threshold.
// --- Per-file min/max peak pyramid -----------------------------------------
// The whole file is scanned ONCE into fixed-stride min/max arrays (cached on
// the AudioBuffer via a WeakMap). Every clip's waveform — and every zoom
// change — then aggregates from this small pyramid instead of re-scanning
// hundreds of thousands of raw Float32 samples, which is what made waveform
// rendering feel slow (the raw scan ran per clip, per zoom, per DPR change).
const PEAK_STRIDE = 256; // raw samples per pyramid entry (~5ms @ 48kHz)
const _filePeaks = new WeakMap(); // AudioBuffer -> { min, max, stride, sr }

function getFilePeaks(audioBuffer) {
  let fp = _filePeaks.get(audioBuffer);
  if (fp) return fp;
  const ch = audioBuffer.getChannelData(0);
  const n = ch.length;
  const count = Math.max(1, Math.ceil(n / PEAK_STRIDE));
  const min = new Float32Array(count);
  const max = new Float32Array(count);
  for (let b = 0; b < count; b++) {
    const s0 = b * PEAK_STRIDE;
    const s1 = Math.min(n, s0 + PEAK_STRIDE);
    let mn = Infinity;
    let mx = -Infinity;
    for (let i = s0; i < s1; i++) {
      const v = ch[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    min[b] = mn === Infinity ? 0 : mn;
    max[b] = mx === -Infinity ? 0 : mx;
  }
  fp = { min, max, stride: PEAK_STRIDE, sr: audioBuffer.sampleRate };
  _filePeaks.set(audioBuffer, fp);
  return fp;
}

export function computePeaks(audioBuffer, startSec, endSec, peakCount) {
  if (!audioBuffer || peakCount <= 0) return null;
  const fp = getFilePeaks(audioBuffer);
  const perSec = fp.sr / fp.stride;
  const p0 = Math.max(0, Math.floor(startSec * perSec));
  const p1 = Math.min(fp.min.length, Math.ceil(endSec * perSec));
  const span = p1 - p0;
  if (span <= 0) return null;
  const buckets = Math.min(peakCount, span);
  const perBucket = span / buckets;
  const out = new Float32Array(buckets * 2);
  for (let b = 0; b < buckets; b++) {
    const b0 = p0 + Math.floor(b * perBucket);
    const b1 = p0 + Math.floor((b + 1) * perBucket);
    let mn = Infinity;
    let mx = -Infinity;
    for (let i = b0; i < b1; i++) {
      if (fp.min[i] < mn) mn = fp.min[i];
      if (fp.max[i] > mx) mx = fp.max[i];
    }
    if (mn === Infinity) { mn = 0; mx = 0; }
    out[b * 2] = mn;
    out[b * 2 + 1] = mx;
  }
  return out;
}

export class AudioEngine {
  constructor() {
    this.master = ctx().createGain();
    this.master.gain.value = 1;
    this.master.connect(ctx().destination);
    this.trackGains = new Map(); // trackId → GainNode
    this._scheduled = []; // {node, stopAt}
    this._playing = false;
    this._startedAt = 0; // AudioContext time when transport started
    this._startOffset = 0; // project-time at which transport started
    this._endsAt = 0; // AudioContext time of the latest scheduled stop
    this._onEnd = null;
    this._playbackRate = 1; // pitch-preserved speed multiplier
  }

  getPlaybackRate() {
    return this._playbackRate;
  }

  // Change playback speed (pitch preserved). Triggers a re-schedule
  // from the current project time if playback is in progress AND a
  // project is provided, so the caller doesn't have to stop/start.
  // Returns the rate actually applied (clamped to [0.25, 4]).
  async setPlaybackRate(rate, project = null) {
    const r = Math.max(0.25, Math.min(4, Number(rate) || 1));
    if (r === this._playbackRate) return r;
    this._playbackRate = r;
    if (this._playing && project) {
      const at = this.currentTime();
      const onEnd = this._onEnd;
      this.stop();
      try {
        await this.play(project, at, { onEnd });
      } catch {}
    }
    return r;
  }

  // Pre-build the stretched buffer for every unique source at the
  // current rate. Called from prepare() and whenever the rate changes.
  // Returns a Promise that resolves when every buffer is ready (or
  // rejects on the first failure). Splits work across microtasks to
  // keep the UI responsive on long projects.
  async _ensureStretched(project) {
    if (this._playbackRate === 1) return;
    const files = new Set();
    for (const t of project.tracks) {
      if (t.sourceFile && sourceCache.has(t.sourceFile)) files.add(t.sourceFile);
    }
    for (const f of files) {
      const key = `${f}::${this._playbackRate}`;
      if (stretchedCache.has(key)) continue;
      // Yield to the event loop between files so the renderer can
      // paint a "stretching…" indicator and remain responsive.
      await new Promise((res) => setTimeout(res, 0));
      getOrBuildStretched(f, this._playbackRate);
    }
  }

  destroy() {
    this.stop();
    this.trackGains.forEach((g) => {
      try { g.disconnect(); } catch {}
    });
    this.trackGains.clear();
    try { this.master.disconnect(); } catch {}
  }

  _gainFor(trackId) {
    let g = this.trackGains.get(trackId);
    if (!g) {
      g = ctx().createGain();
      g.connect(this.master);
      this.trackGains.set(trackId, g);
    }
    return g;
  }

  setTrackGain(trackId, value) {
    const g = this._gainFor(trackId);
    g.gain.setValueAtTime(Math.max(0, Math.min(4, Number(value) || 0)), ctx().currentTime);
  }

  muteTrack(trackId, muted) {
    const g = this._gainFor(trackId);
    g.gain.setValueAtTime(muted ? 0 : 1, ctx().currentTime);
  }

  // Pre-decode every unique source referenced by the project so play()
  // is instant. Returns when every source is in cache (or rejected).
  // Also builds stretched buffers if the current rate is not 1.
  // Decode every source at full quality, for playback and the analysis
  // features (finetune, hidden-audio). Returns the files that could not be
  // read instead of throwing: one missing take should not stop the rest of
  // the project from playing.
  async prepare(project, onProgress) {
    const files = new Set();
    for (const t of project.tracks) {
      if (t.sourceFile) files.add(t.sourceFile);
    }
    const list = [...files];
    const total = list.length;
    let done = 0;
    if (onProgress) onProgress({ done, total });
    const results = await Promise.allSettled(list.map((f) => decodeFile(f).finally(() => {
      done += 1;
      if (onProgress) onProgress({ done, total });
    })));
    await this._ensureStretched(project);
    return results
      .map((r, i) => (r.status === 'rejected' ? { file: list[i], error: r.reason } : null))
      .filter(Boolean);
  }

  isPlaying() {
    return this._playing;
  }

  // Master timeline position right now, in seconds.
  currentTime() {
    if (!this._playing) return this._startOffset;
    // Wall-clock elapsed × rate = project-time elapsed. The stretched
    // buffers compress wall-clock; project time still advances at the
    // user's expected pace.
    return this._startOffset + (ctx().currentTime - this._startedAt) * this._playbackRate;
  }

  async play(project, fromTime = 0, { onEnd } = {}) {
    if (this._playing) this.stop();
    if (ctx().state === 'suspended') await ctx().resume();

    // Sources are decoded lazily now, so make sure they exist before
    // scheduling. Already-decoded files make this a no-op.
    await this.prepare(project);

    // Ensure stretched buffers exist for the current rate before we
    // start scheduling. _ensureStretched is a no-op when rate === 1.
    await this._ensureStretched(project);

    const rate = this._playbackRate;
    const anySolo = project.tracks.some((t) => t.solo);
    const t0 = ctx().currentTime;
    const startOffset = Math.max(0, Number(fromTime) || 0);
    let lastStop = t0;

    for (const track of project.tracks) {
      // Solo masking: when at least one track is soloed, only soloed
      // tracks are audible. Otherwise muted tracks are silenced.
      const audible = anySolo ? track.solo : !track.muted;
      const g = this._gainFor(track.id);
      g.gain.setValueAtTime(audible ? Math.max(0, Number(track.gain) || 0) : 0, t0);

      // At rate=1 use the cached source directly; otherwise use the
      // pitch-preserved stretched copy whose internal duration is
      // original/rate. All clip offsets/durations are likewise scaled
      // by 1/rate so the source-time range we want plays in the
      // matching wall-clock span.
      const ab = rate === 1
        ? sourceCache.get(track.sourceFile)
        : getOrBuildStretched(track.sourceFile, rate);
      if (!ab) continue; // not decoded — skip silently

      for (const clip of track.clips) {
        const clipStart = clip.startTime;
        const clipEnd = clip.startTime + clip.duration;
        if (clipEnd <= startOffset) continue; // already past
        const enterAtProject = Math.max(clipStart, startOffset);
        const offsetIntoSource = (clip.sourceStart + (enterAtProject - clipStart)) / rate;
        const playFor = (clipEnd - enterAtProject) / rate;
        if (playFor <= 0.01) continue;

        const when = t0 + (enterAtProject - startOffset) / rate;
        const node = ctx().createBufferSource();
        node.buffer = ab;
        node.connect(g);
        try {
          node.start(when, Math.max(0, offsetIntoSource), playFor);
        } catch {
          continue;
        }
        const stopAt = when + playFor;
        this._scheduled.push({ node, stopAt });
        lastStop = Math.max(lastStop, stopAt);
      }
    }

    this._playing = true;
    this._startedAt = t0;
    this._startOffset = startOffset;
    this._endsAt = lastStop;
    this._onEnd = onEnd || null;

    // Auto-stop callback when the last scheduled clip ends.
    const tailMs = Math.max(0, (lastStop - t0) * 1000);
    this._endTimer = setTimeout(() => {
      if (this._playing) {
        this._playing = false;
        if (this._onEnd) this._onEnd();
      }
    }, tailMs + 30);
  }

  pause() {
    if (!this._playing) return;
    const now = ctx().currentTime;
    const projectTime = this._startOffset + (now - this._startedAt) * this._playbackRate;
    this.stop();
    this._startOffset = projectTime;
  }

  stop() {
    if (this._endTimer) {
      clearTimeout(this._endTimer);
      this._endTimer = null;
    }
    for (const { node } of this._scheduled) {
      try { node.stop(); } catch {}
      try { node.disconnect(); } catch {}
    }
    this._scheduled = [];
    this._playing = false;
    this._onEnd = null;
  }
}
